import type { AdSlotPlacement } from '@shopgnd/shared';

export type PlacementGroup = 'homepage' | 'browse' | 'sidebar' | 'other';

export interface PlacementSpec {
  key: AdSlotPlacement;
  label: string;
  widthPx: number;
  heightPx: number;
  group: PlacementGroup;
  page: string;
  description: string;
  available: boolean;
}

export const PLACEMENT_SPECS: PlacementSpec[] = [
  {
    key: 'homepage_hero',
    label: 'Homepage billboard',
    widthPx: 1440,
    heightPx: 600,
    group: 'homepage',
    page: 'Homepage carousel',
    description: 'Premium hero slide — seen by every visitor.',
    available: true,
  },
  {
    key: 'homepage_grid',
    label: 'Homepage feature grid',
    widthPx: 600,
    heightPx: 600,
    group: 'homepage',
    page: 'Homepage',
    description: 'Featured tile in the homepage product grid.',
    available: true,
  },
  {
    key: 'homepage_sidebar_square',
    label: 'Homepage sidebar — square',
    widthPx: 600,
    heightPx: 600,
    group: 'sidebar',
    page: 'Homepage',
    description: 'Square unit alongside category tiles and deals.',
    available: true,
  },
  {
    key: 'homepage_footer',
    label: 'Homepage footer promo',
    widthPx: 1440,
    heightPx: 120,
    group: 'homepage',
    page: 'Homepage',
    description: 'Wide sponsored strip below “Why ShopGND” (rotates active creatives).',
    available: true,
  },
  {
    key: 'category_top',
    label: 'Category page banner',
    widthPx: 1440,
    heightPx: 300,
    group: 'browse',
    page: 'Shop / category',
    description: 'Full-width strip when shoppers browse a department.',
    available: true,
  },
  {
    key: 'search_inline',
    label: 'Search results inline',
    widthPx: 1440,
    heightPx: 300,
    group: 'browse',
    page: 'Shop / search',
    description: 'Inline banner within search results.',
    available: true,
  },
  {
    key: 'shop_sidebar_square',
    label: 'Shop sidebar — square',
    widthPx: 600,
    heightPx: 600,
    group: 'sidebar',
    page: 'Marketplace browse',
    description: 'Sticky square in the shop sidebar (desktop).',
    available: true,
  },
  {
    key: 'shop_sidebar_rectangle',
    label: 'Shop sidebar — rectangle',
    widthPx: 600,
    heightPx: 800,
    group: 'sidebar',
    page: 'Marketplace browse',
    description: 'Taller sidebar unit below the square slot.',
    available: true,
  },
  {
    key: 'shop_sidebar_halfpage',
    label: 'Shop sidebar — half page',
    widthPx: 600,
    heightPx: 1440,
    group: 'sidebar',
    page: 'Marketplace browse',
    description: 'Full-height sticky sidebar on shop pages.',
    available: true,
  },
  {
    key: 'product_sidebar_square',
    label: 'Product page sidebar — square',
    widthPx: 600,
    heightPx: 600,
    group: 'sidebar',
    page: 'Product detail',
    description: 'Square ad alongside product information.',
    available: true,
  },
  {
    key: 'product_sidebar_rectangle',
    label: 'Product page sidebar — rectangle',
    widthPx: 600,
    heightPx: 800,
    group: 'sidebar',
    page: 'Product detail',
    description: 'Taller unit on product detail pages.',
    available: true,
  },
  {
    key: 'store_sidebar_rectangle',
    label: 'Store page sidebar',
    widthPx: 600,
    heightPx: 800,
    group: 'sidebar',
    page: 'Vendor store',
    description: 'Sidebar on public vendor storefront pages.',
    available: true,
  },
  {
    key: 'findapro_sidebar_square',
    label: 'Find a Pro sidebar',
    widthPx: 600,
    heightPx: 600,
    group: 'sidebar',
    page: 'Find a Pro',
    description: 'Square sidebar on the service directory.',
    available: true,
  },
  {
    key: 'classifieds_sidebar_square',
    label: 'Classifieds sidebar',
    widthPx: 600,
    heightPx: 600,
    group: 'sidebar',
    page: 'Classifieds',
    description: 'Square sidebar on classified listings.',
    available: true,
  },
  {
    key: 'vendors_sidebar_square',
    label: 'Business directory — square',
    widthPx: 600,
    heightPx: 600,
    group: 'sidebar',
    page: 'Business directory',
    description: 'Square sidebar on the businesses page (desktop).',
    available: true,
  },
  {
    key: 'vendors_sidebar_rectangle',
    label: 'Business directory — rectangle',
    widthPx: 600,
    heightPx: 800,
    group: 'sidebar',
    page: 'Business directory',
    description: 'Taller sidebar unit on the businesses page.',
    available: true,
  },
  {
    key: 'property_sidebar_square',
    label: 'Property hub — square',
    widthPx: 600,
    heightPx: 600,
    group: 'sidebar',
    page: 'Property',
    description: 'Square sidebar on property listings.',
    available: true,
  },
  {
    key: 'property_sidebar_rectangle',
    label: 'Property hub — rectangle',
    widthPx: 600,
    heightPx: 800,
    group: 'sidebar',
    page: 'Property',
    description: 'Taller sidebar on property listings.',
    available: true,
  },
  {
    key: 'vendors_page_banner',
    label: 'Business directory — top banner',
    widthPx: 1440,
    heightPx: 120,
    group: 'browse',
    page: 'Business directory',
    description: 'Full-width sponsored strip above business listings.',
    available: true,
  },
  {
    key: 'shop_page_banner',
    label: 'Shop browse — top banner',
    widthPx: 1440,
    heightPx: 120,
    group: 'browse',
    page: 'Marketplace browse',
    description: 'Full-width sponsored strip above shop results.',
    available: true,
  },
  {
    key: 'deals_page_banner',
    label: 'Deals — top banner',
    widthPx: 1440,
    heightPx: 120,
    group: 'browse',
    page: 'Deals',
    description: 'Full-width sponsored strip on the deals page.',
    available: true,
  },
  {
    key: 'property_page_banner',
    label: 'Property hub — top banner',
    widthPx: 1440,
    heightPx: 120,
    group: 'browse',
    page: 'Property',
    description: 'Full-width sponsored strip above property listings.',
    available: true,
  },
  {
    key: 'classifieds_page_banner',
    label: 'Classifieds — top banner',
    widthPx: 1440,
    heightPx: 120,
    group: 'browse',
    page: 'Classifieds',
    description: 'Full-width sponsored strip above classified listings.',
    available: true,
  },
  {
    key: 'vendors_page_inline',
    label: 'Business directory — inline',
    widthPx: 1440,
    heightPx: 120,
    group: 'browse',
    page: 'Business directory',
    description: 'Inline promo card within the business directory feed.',
    available: true,
  },
  {
    key: 'classifieds_page_inline',
    label: 'Classifieds — inline',
    widthPx: 1440,
    heightPx: 120,
    group: 'browse',
    page: 'Classifieds',
    description: 'Inline promo card within classified search results.',
    available: true,
  },
  {
    key: 'business_profile_inline',
    label: 'Business profile — inline',
    widthPx: 1440,
    heightPx: 120,
    group: 'other',
    page: 'Vendor store',
    description: 'Inline promo on public business profile pages.',
    available: true,
  },
];

export const PLACEMENT_GROUP_LABELS: Record<PlacementGroup, string> = {
  homepage: 'Homepage placements',
  browse: 'Browse & search placements',
  sidebar: 'Sidebar ads',
  other: 'Other services',
};

export const OTHER_AD_SERVICES = [
  { id: 'store_spotlight', label: 'Sponsored store spotlight' },
  { id: 'newsletter', label: 'Email newsletter mention' },
  { id: 'social_post', label: 'Sponsored post on Instagram / Facebook' },
  { id: 'video', label: 'Video production' },
  { id: 'live_stream', label: 'Video live streaming' },
  { id: 'other', label: 'Other (describe in comments)' },
] as const;

/** Placements with live rendering in the web app (admin can still book others — shown as coming soon). */
export const LIVE_AD_PLACEMENTS = new Set<AdSlotPlacement>([
  'homepage_footer',
  'shop_sidebar_square',
  'shop_sidebar_rectangle',
  'product_sidebar_square',
  'product_sidebar_rectangle',
  'store_sidebar_rectangle',
  'classifieds_sidebar_square',
  'vendors_sidebar_square',
  'vendors_sidebar_rectangle',
  'property_sidebar_square',
  'property_sidebar_rectangle',
  'shop_page_banner',
  'deals_page_banner',
  'vendors_page_banner',
  'classifieds_page_banner',
  'vendors_page_inline',
  'classifieds_page_inline',
]);

export function isAdPlacementLive(key: AdSlotPlacement): boolean {
  return LIVE_AD_PLACEMENTS.has(key);
}

export function specsByGroup(group: PlacementGroup): PlacementSpec[] {
  return PLACEMENT_SPECS.filter((s) => s.group === group).map((s) => ({
    ...s,
    available: isAdPlacementLive(s.key),
  }));
}

export function specForPlacement(key: AdSlotPlacement): PlacementSpec | undefined {
  return PLACEMENT_SPECS.find((s) => s.key === key);
}

export function formatDimensions(spec: PlacementSpec): string {
  return `${spec.widthPx} × ${spec.heightPx} px`;
}

/** Promotional ad units for listing surfaces — loaded from BannerAd via usePromoAd. */
export type AdUnitPage = 'vendors' | 'property' | 'classifieds' | 'business-profile' | 'shop' | 'deals';

export type AdUnit = {
  id: string;
  advertiserId: string;
  advertiserName: string;
  advertiserInitials: string;
  logoBackground?: string;
  logoColor?: string;
  headline: string;
  subline: string;
  ctaLabel: string;
  ctaHref: string;
  variant?: 'gold' | 'blue' | 'green' | 'default';
  placement: 'banner' | 'sidebar' | 'inline';
  pages: AdUnitPage[];
};

const PROMO_BANNER_PLACEMENTS: Partial<Record<AdUnitPage, AdSlotPlacement>> = {
  vendors: 'vendors_page_banner',
  shop: 'shop_page_banner',
  deals: 'deals_page_banner',
  property: 'property_page_banner',
  classifieds: 'classifieds_page_banner',
};

const PROMO_INLINE_PLACEMENTS: Partial<Record<AdUnitPage, AdSlotPlacement>> = {
  vendors: 'vendors_page_inline',
  classifieds: 'classifieds_page_inline',
  'business-profile': 'business_profile_inline',
};

export function promoPlacementForPage(
  page: AdUnitPage,
  kind: AdUnit['placement'],
): AdSlotPlacement | null {
  if (kind === 'banner') return PROMO_BANNER_PLACEMENTS[page] ?? null;
  if (kind === 'inline') return PROMO_INLINE_PLACEMENTS[page] ?? null;
  return null;
}

export type AdFeedMarker<T> = T | { type: 'ad'; ad: AdUnit };

export function injectInlineAds<T>(
  items: T[],
  ad: AdUnit,
  everyN = 5,
): AdFeedMarker<T>[] {
  if (items.length < 2) return [...items];
  const out: AdFeedMarker<T>[] = [];
  items.forEach((item, i) => {
    if (i > 0 && i % everyN === 0 && i < items.length) {
      out.push({ type: 'ad', ad });
    }
    out.push(item);
  });
  return out;
}
